/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useRef } from "react";
import mapboxgl, { type Map as MapboxMap } from "mapbox-gl";
import SunCalc from "suncalc";
import { Flex, type Timeout, useDelayedState } from "@huub-web/ui";

import { removeSymbolLayers } from "../../MapboxMapView/utils/mapLayers";
import { MAP_BUILDING_COLOR_MODE_STREETS } from "../../MapboxMapView/config/mapColors";
import { MemoizedHotspotController } from "../../MapboxMapView/components/MemoizedHotspotController";
import { ZoomControls } from "../../MapboxMapView/components/ZoomControls";
import { createZoomHandlers } from "../../MapboxMapView/utils/mapControls";

import { containerStyle } from "./DynamicSundialMap.css";
import {
  controlsContainerStyle,
  zoomControlsStyle,
} from "../../MapboxMapView/MapboxMapView.css";

export type SundialTestProps = {
  /**
   * Center coordinates [latitude, longitude]
   */
  center: [number, number];
  /**
   * Mapbox access token
   */
  accessToken: string;
  /**
   * Callback function to execute when map is ready
   */
  onReady?: () => void;
  /**
   * Time for sun position
   */
  time: Date;
};

/**
 * DynamicSundialMap
 */
export const DynamicSundialMap = ({
  center,
  accessToken,
  time,
  onReady,
}: SundialTestProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const timeRef = useRef<Date>(time);

  const [isMapReady, setIsMapReady] = useDelayedState(false, 500);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }

    mapboxgl.accessToken = accessToken;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      zoom: 17,
      minZoom: 16,
      maxZoom: 20,
      pitch: 45,
      center: [center[1], center[0]],
      dragPan: false,
      dragRotate: false,
      touchZoomRotate: true,
      doubleClickZoom: false,
      scrollZoom: false,
    });

    mapRef.current = map;

    // Custom double-click zoom - stays at current position
    map.on("dblclick", () => {
      const currentZoom = map.getZoom();
      map.easeTo({
        zoom: currentZoom + 1,
        center: map.getCenter(), // Lock to current center
        duration: 300,
      });
    });

    // Debounced zoom end handler to prevent animation conflicts
    let zoomTimeout: Timeout | null = null;
    let isRecentering = false;

    map.on("zoomstart", () => {
      // Cancel any pending recenter when new zoom starts
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
        zoomTimeout = null;
      }
      isRecentering = false;
    });

    map.on("zoomend", () => {
      // Clear any existing timeout
      if (zoomTimeout) {
        clearTimeout(zoomTimeout);
      }

      // Wait for zoom to truly settle before recentering
      zoomTimeout = setTimeout(() => {
        if (!isRecentering && map) {
          isRecentering = true;
          // console.log("zoomend");
          // map.flyTo({
          //   center: [center[1], center[0]],
          //   duration: 400,
          //   essential: true,
          // });
          map.setCenter([center[1], center[0]]);
          // Reset flag after animation completes
          setTimeout(() => {
            isRecentering = false;
          }, 450);
        }
        zoomTimeout = null;
      }, 250);
    });

    // Custom drag to rotate handler
    let isDragging = false;
    let startX = 0;
    let startBearing = 0;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return; // Only left click
      isDragging = true;
      startX = e.clientX;
      startBearing = map.getBearing();
      map.getCanvas().style.cursor = "grabbing";
      e.preventDefault();
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - startX;
      // Convert horizontal movement to rotation (adjust sensitivity as needed)
      const newBearing = startBearing + deltaX * 0.5;
      map.setBearing(newBearing);
      e.preventDefault();
    };

    const onMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        map.getCanvas().style.cursor = "grab";
      }
    };

    const canvas = map.getCanvas();
    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("mouseleave", onMouseUp);

    const createShadowLayer = () => {
      const layer: any = {
        id: "shadow-layer",
        type: "custom" as const,
        renderingMode: "3d" as const,
        map: map,

        onAdd(this: any, map: MapboxMap, gl: WebGLRenderingContext) {
          this.map = map;

          // REAL vertex shader from actual sundial.js (lines 422-446)
          const vertexSource = `
            attribute vec4 a_pos_normal_ed;
            attribute lowp vec2 a_base;
            attribute lowp vec2 a_height;
            uniform mat4 u_matrix;
            uniform float u_azimuth;
            uniform float u_altitude;
            uniform float u_height_factor;

            void main() {
              float base = max(a_base.x, a_base.y);
              float height = max(a_height.x, a_height.y);
              vec4 pos_nx = floor(a_pos_normal_ed * 0.5);
              vec4 top_up_ny_start = a_pos_normal_ed - 2.0 * pos_nx;
              vec3 top_up_ny = top_up_ny_start.xyz;
              float t = top_up_ny.x;
              vec4 pos = vec4(pos_nx.xy, t > 0.0 ? height : base, 1);
              float len = pos.z * u_height_factor / tan(u_altitude);
              pos.x += cos(u_azimuth) * len;
              pos.y += sin(u_azimuth) * len;
              pos.z = 0.0;
              gl_Position = u_matrix * pos;
            }
          `;

          // REAL fragment shader from actual sundial.js (lines 442-446)
          const fragmentSource = `
            void main() {
              gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
            }
          `;

          // Compile main shader
          const vertexShader = gl.createShader(gl.VERTEX_SHADER);
          if (!vertexShader) return;
          gl.shaderSource(vertexShader, vertexSource);
          gl.compileShader(vertexShader);

          const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
          if (!fragmentShader) return;
          gl.shaderSource(fragmentShader, fragmentSource);
          gl.compileShader(fragmentShader);

          const program = gl.createProgram();
          if (!program) return;
          this.program = program;
          gl.attachShader(this.program, vertexShader);
          gl.attachShader(this.program, fragmentShader);
          gl.linkProgram(this.program);
          gl.validateProgram(this.program);

          this.uMatrix = gl.getUniformLocation(this.program, "u_matrix");
          this.uHeightFactor = gl.getUniformLocation(
            this.program,
            "u_height_factor",
          );
          this.uAltitude = gl.getUniformLocation(this.program, "u_altitude");
          this.uAzimuth = gl.getUniformLocation(this.program, "u_azimuth");
          this.aPosNormalEd = gl.getAttribLocation(
            this.program,
            "a_pos_normal_ed",
          );
          this.aBase = gl.getAttribLocation(this.program, "a_base");
          this.aHeight = gl.getAttribLocation(this.program, "a_height");

          // Compile opacity program (pass 2) - from sundial.js lines 473-533
          const opacityVertexSource = `
            attribute vec2 a_pos;
            varying vec2 v_pos;
            void main() {
              v_pos = a_pos * 0.5 + 0.5;
              gl_Position = vec4(a_pos, 0, 1);
            }
          `;

          const opacityFragmentSource = `
            precision mediump float;
            uniform sampler2D u_tex;
            uniform float u_alt;
            uniform vec4 u_shadeColor;
            varying vec2 v_pos;
            void main() {
              if (u_alt < 0.0) {
                gl_FragColor = u_shadeColor;
              } else {
                vec4 color = texture2D(u_tex, v_pos);
                if (color == vec4(0, 0, 0, 0)) {
                  gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
                } else {
                  gl_FragColor = u_shadeColor;
                }
              }
            }
          `;

          const opacityVertexShader = gl.createShader(gl.VERTEX_SHADER);
          if (!opacityVertexShader) return;
          gl.shaderSource(opacityVertexShader, opacityVertexSource);
          gl.compileShader(opacityVertexShader);

          const opacityFragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
          if (!opacityFragmentShader) return;
          gl.shaderSource(opacityFragmentShader, opacityFragmentSource);
          gl.compileShader(opacityFragmentShader);

          const opacityProgram = gl.createProgram();
          if (!opacityProgram) return;
          this.opacityProgram = opacityProgram;
          gl.attachShader(this.opacityProgram, opacityVertexShader);
          gl.attachShader(this.opacityProgram, opacityFragmentShader);
          gl.linkProgram(this.opacityProgram);
          gl.validateProgram(this.opacityProgram);

          this.aPos = gl.getAttribLocation(this.opacityProgram, "a_pos");
          this.uTex = gl.getUniformLocation(this.opacityProgram, "u_tex");
          this.uAlt = gl.getUniformLocation(this.opacityProgram, "u_alt");
          this.uMatrix2 = gl.getUniformLocation(
            this.opacityProgram,
            "u_matrix",
          );
          this.shadeColor = gl.getUniformLocation(
            this.opacityProgram,
            "u_shadeColor",
          );

          // Create framebuffer
          const viewport = gl.getParameter(gl.VIEWPORT);
          const vWidth = viewport[2];
          const vHeight = viewport[3];

          this.buf = gl.createBuffer();
          gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
          const screen = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
          gl.bufferData(gl.ARRAY_BUFFER, screen, gl.STATIC_DRAW);

          this.tex = gl.createTexture();
          gl.bindTexture(gl.TEXTURE_2D, this.tex);
          gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            vWidth,
            vHeight,
            0,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            null,
          );
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

          this.fb = gl.createFramebuffer();
          this.attachmentPoint = gl.COLOR_ATTACHMENT0;

          // Store viewport dimensions for resize detection
          this.vWidth = vWidth;
          this.vHeight = vHeight;

          this.renderCount = 0;

          // console.log("✅ [SundialMap] Shadow layer initialized");
        },

        render(this: any, gl: WebGLRenderingContext) {
          if (!this.program || !this.opacityProgram) return;

          this.renderCount = (this.renderCount || 0) + 1;

          // Check for viewport resize and recreate texture if needed
          const currentViewport = gl.getParameter(gl.VIEWPORT);
          const currentWidth = currentViewport[2];
          const currentHeight = currentViewport[3];

          if (this.vWidth !== currentWidth || this.vHeight !== currentHeight) {
            // console.log(`🔄 [SundialMap] Viewport resized: ${this.vWidth}x${this.vHeight} → ${currentWidth}x${currentHeight}`);

            // Update stored dimensions
            this.vWidth = currentWidth;
            this.vHeight = currentHeight;

            // Recreate texture with new dimensions
            gl.bindTexture(gl.TEXTURE_2D, this.tex);
            gl.texImage2D(
              gl.TEXTURE_2D,
              0,
              gl.RGBA,
              currentWidth,
              currentHeight,
              0,
              gl.RGBA,
              gl.UNSIGNED_BYTE,
              null,
            );
            gl.texParameteri(
              gl.TEXTURE_2D,
              gl.TEXTURE_WRAP_S,
              gl.CLAMP_TO_EDGE,
            );
            gl.texParameteri(
              gl.TEXTURE_2D,
              gl.TEXTURE_WRAP_T,
              gl.CLAMP_TO_EDGE,
            );
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
          }

          // Only log first 5 renders for comparison
          if (this.renderCount <= 5) {
            // console.log(
            //   `\n🎬 [SundialMap] RENDER #${this.renderCount} START ============`,
            // );
          }

          // Pass 1: Render shadows to framebuffer (sundial.js lines 560-655)
          // biome-ignore lint/correctness/useHookAtTopLevel: gl.useProgram is a WebGL call, not a React hook
          gl.useProgram(this.program);
          gl.bindFramebuffer(gl.FRAMEBUFFER, this.fb);
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.tex);
          gl.framebufferTexture2D(
            gl.FRAMEBUFFER,
            this.attachmentPoint,
            gl.TEXTURE_2D,
            this.tex,
            0,
          );
          gl.clear(gl.COLOR_BUFFER_BIT);

          const sourceCaches = this.map.style._sourceCaches;
          const source =
            sourceCaches["fill-extrusion:composite"] ||
            sourceCaches["other:composite"] ||
            sourceCaches.composite;

          if (!source) {
            if (this.renderCount <= 5)
              console.warn(`  ❌ [SundialMap] No source cache`);
            return;
          }

          const coords = source.getVisibleCoordinates().reverse();
          const buildingsLayer = this.map.getLayer("3d-buildings");
          if (!buildingsLayer) {
            if (this.renderCount <= 5)
              console.warn(`  ❌ [SundialMap] No 3d-buildings layer`);
            return;
          }

          const context = this.map.painter.context;
          const { lng, lat } = this.map.getCenter();
          const pos = SunCalc.getPosition(timeRef.current, lat, lng);

          if (this.renderCount <= 5) {
            // console.log(
            //   `  📍 [SundialMap] Tiles: ${coords.length}, Altitude: ${((pos.altitude * 180) / Math.PI).toFixed(1)}°`,
            // );
          }

          gl.uniform1f(this.uAltitude, pos.altitude);
          gl.uniform1f(this.uAzimuth, pos.azimuth + (3 * Math.PI) / 2);

          for (const coord of coords) {
            const tile = source.getTile(coord);
            const bucket = tile.buckets?.[buildingsLayer.id];
            if (!bucket) continue;

            const [heightBuffer, baseBuffer] = (bucket as any)
              .programConfigurations.programConfigurations["3d-buildings"]
              ._buffers;

            gl.uniformMatrix4fv(this.uMatrix, false, coord.projMatrix as any);
            gl.uniformMatrix4fv(this.uMatrix2, false, coord.projMatrix as any);
            gl.uniform1f(
              this.uHeightFactor,
              2 ** coord.overscaledZ / tile.tileSize / 8,
            );

            for (const segment of (bucket as any).segments.get()) {
              const numPrevAttrib = (context as any).currentNumAttributes || 0;
              const numNextAttrib = 2;
              for (let i = numNextAttrib; i < numPrevAttrib; i++) {
                gl.disableVertexAttribArray(i);
              }

              const vertexOffset = segment.vertexOffset || 0;
              gl.enableVertexAttribArray(this.aPosNormalEd);
              gl.enableVertexAttribArray(this.aHeight);
              gl.enableVertexAttribArray(this.aBase);

              (bucket as any).layoutVertexBuffer.bind();
              gl.vertexAttribPointer(
                this.aPosNormalEd,
                4,
                gl.SHORT,
                false,
                8,
                8 * vertexOffset,
              );

              heightBuffer.bind();
              gl.vertexAttribPointer(
                this.aHeight,
                1,
                gl.FLOAT,
                false,
                4,
                4 * vertexOffset,
              );

              baseBuffer.bind();
              gl.vertexAttribPointer(
                this.aBase,
                1,
                gl.FLOAT,
                false,
                4,
                4 * vertexOffset,
              );

              (bucket as any).indexBuffer.bind();
              (context as any).currentNumAttributes = numNextAttrib;
              gl.drawElements(
                gl.TRIANGLES,
                segment.primitiveLength * 3,
                gl.UNSIGNED_SHORT,
                segment.primitiveOffset * 3 * 2,
              );
            }
          }

          if (this.renderCount <= 5) {
            // console.log(`  ✅ [TEST] Pass 1 complete`);
          }

          // Pass 2: Render with transparency (sundial.js lines 657-667)
          if (this.renderCount <= 5) {
            // console.log(`  🟣 [TEST] Pass 2 START`);
          }

          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          // biome-ignore lint/correctness/useHookAtTopLevel: gl.useProgram is a WebGL call, not a React hook
          gl.useProgram(this.opacityProgram);
          gl.uniform1f(this.uAlt, pos.altitude);
          gl.uniform4fv(this.shadeColor, new Float32Array([0, 0, 0, 0.6]));
          gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
          gl.enableVertexAttribArray(this.aPos);
          gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

          // Enable blending for transparency
          gl.enable(gl.BLEND);
          gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
          gl.disable(gl.DEPTH_TEST);

          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

          // Restore state
          gl.enable(gl.DEPTH_TEST);
          gl.disable(gl.BLEND);
        },

        onRemove(this: any, _map: MapboxMap, gl: WebGLRenderingContext) {
          if (this.program) gl.deleteProgram(this.program);
          if (this.opacityProgram) gl.deleteProgram(this.opacityProgram);
          if (this.fb) gl.deleteFramebuffer(this.fb);
          if (this.tex) gl.deleteTexture(this.tex);
          if (this.buf) gl.deleteBuffer(this.buf);
        },
      };

      return layer;
    };

    map.on("load", () => {
      if (map.getLayer("building")) {
        map.removeLayer("building");
      }

      // Add 3D buildings first
      map.addLayer({
        id: "3d-buildings",
        source: "composite",
        "source-layer": "building",
        type: "fill-extrusion",
        paint: {
          "fill-extrusion-color": MAP_BUILDING_COLOR_MODE_STREETS,
          "fill-extrusion-height": ["number", ["get", "height"], 5],
          "fill-extrusion-base": ["number", ["get", "min_height"], 0],
          "fill-extrusion-opacity": 1,
        },
      });

      // Add shadow layer before 3D buildings so it renders underneath
      map.addLayer(createShadowLayer() as any, "3d-buildings");

      onReady?.();
      setIsMapReady(true);
    });

    map.on("style.load", () => {
      removeSymbolLayers(map);
    });

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("mouseleave", onMouseUp);
      map.remove();
    };
  }, [center, accessToken]);

  useEffect(() => {
    timeRef.current = time;
    if (mapRef.current) {
      mapRef.current.triggerRepaint();
    }
  }, [time]);

  const { handleZoomIn, handleZoomOut } = createZoomHandlers(mapRef);

  return (
    <>
      <div
        ref={mapContainerRef}
        className={containerStyle({ loaded: isMapReady })}
      />
      {isMapReady && (
        <MemoizedHotspotController
          hotspot={{
            id: "sundial-center-hotspot",
            lat: center[0],
            lng: center[1],
            type: "marker",
          }}
          mapboxgl={mapboxgl}
          mapInstanceRef={mapRef}
        />
      )}
      <Flex
        direction="row"
        gap="s"
        className={controlsContainerStyle}
        justify="flex-end"
        align="center"
      >
        <ZoomControls
          className={zoomControlsStyle}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
        />
      </Flex>
    </>
  );
};
